import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import type { DocumentNode } from 'graphql';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { GenericGroupVariables } from '../../common/types.tsx';

export type BaseGroupItemNode = {
	readonly id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly field?: any | undefined;
	readonly totalCount?: number;
	readonly __typename?: string;
};

// this generic contains `TIssueNode` as one of the property
export type BaseGroupsData<TIssueNode> = {
	readonly groups: {
		readonly nodes: ReadonlyArray<TIssueNode | null | undefined> | null | undefined;
	};
};

// This service is used to update groups in Apollo cache
export const useItemGroupsCacheActions = () => {
	const analyticsTaskName = 'useItemGroupsCacheActionsApolloCacheActions';
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const client = useApolloClient();

	const updateGroupItemInCache = useCallback(
		<TIssueNode extends BaseGroupItemNode, TGroupsData extends BaseGroupsData<TIssueNode>>({
			query,
			variables,
			id,
			updateItem,
		}: {
			id: string;
			query: DocumentNode;
			variables: GenericGroupVariables;
			updateItem: (existingItem: TIssueNode) => TIssueNode | undefined;
		}): void => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery<TGroupsData, GenericGroupVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'updateGroupItemInCache',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});

					return null;
				}
			};
			const data = getCachedListData();

			const existingNodes = data?.groups.nodes;
			if (existingNodes == null) {
				return;
			}

			const itemToUpdateIndex = existingNodes.findIndex((issue) => issue?.id === id) ?? undefined;
			const existingItem = existingNodes[itemToUpdateIndex];

			if (itemToUpdateIndex === undefined || itemToUpdateIndex === -1 || existingItem == null) {
				return;
			}

			const itemOverride = updateItem(existingItem);
			if (itemOverride === undefined) {
				return;
			}

			const newNodes = [...existingNodes];
			newNodes[itemToUpdateIndex] = itemOverride;

			try {
				client.writeQuery({
					query,
					variables,
					// @ts-expect-error - TS2345 - Argument of type '{ query: DocumentNode; variables: GenericGroupVariables; notifyOnNetworkStatusChange: boolean; data: { groups: { nodes: (TIssueNode | null | undefined)[]; }; }; }' is not assignable to parameter of type 'WriteQueryOptions<{ groups: { nodes: (TIssueNode | null | undefined)[]; }; }, GenericGroupVariables>'.
					notifyOnNetworkStatusChange: true,
					data: {
						...data,
						groups: {
							...(data?.groups || {}),
							nodes: newNodes,
						},
					},
				});
				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'updateGroupItemInCache',
						packageName: 'jiraBusinessListHandlers',
						teamName: 'keystone',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(query),
						variables: JSON.stringify(variables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent],
	);

	const addGroupItemToCache = useCallback(
		<TIssueNode extends BaseGroupItemNode, TGroupsData extends BaseGroupsData<TIssueNode>>({
			query,
			variables,
			newNodes,
		}: {
			query: DocumentNode;
			variables: GenericGroupVariables;
			newNodes: TIssueNode[];
		}) => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery<TGroupsData, GenericGroupVariables>({
						query,
						variables,
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'addGroupItemToCache',
							packageName: 'jiraBusinessListHandlers',
							teamName: 'keystone',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(query),
							variables: JSON.stringify(variables),
							eventName: 'apolloCacheRead taskFail',
							error: JSON.stringify(e),
						},
					});
					return null;
				}
			};

			const data = getCachedListData();

			if (
				data == null ||
				data.groups == null ||
				!Array.isArray(newNodes) ||
				newNodes.length === 0 ||
				!Array.isArray(newNodes)
			) {
				return;
			}

			const newGroupsIds = new Set(
				newNodes
					.map((item) => (item != null && item.id != null ? item.id : undefined))
					.filter(Boolean),
			);

			if (data == null || data.groups == null || !Array.isArray(data.groups.nodes)) {
				return;
			}
			try {
				const newGroups =
					data.groups.nodes
						.filter((node) => node != null && node.id != null && !newGroupsIds.has(node.id))
						.concat(newNodes) ?? [];

				client.writeQuery({
					query,
					variables,
					data: {
						...data,
						groups: {
							...data.groups,
							nodes: newGroups,
						},
					},
				});
				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'addGroupItemToCache',
						packageName: 'jiraBusinessListHandlers',
						teamName: 'keystone',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(query),
						variables: JSON.stringify(variables),
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent],
	);

	return {
		updateGroupItemInCache,
		addGroupItemToCache,
	};
};

export type ItemGroupsCacheActionsReturn = ReturnType<typeof useItemGroupsCacheActions>;
export type UpdateGroupItemInCache = ItemGroupsCacheActionsReturn['updateGroupItemInCache'];

export type AddGroupItemToCache = ItemGroupsCacheActionsReturn['addGroupItemToCache'];
